import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileVideo2, Play } from 'lucide-react'
import ViewAllButton from '@/components/ui/ViewAllButton'
import { useLanguage } from '@afsa/shared'
import { type WebPageSection, type PhotoGallery } from '@/lib/api'
import { useGalleryHighlights } from '@/lib/api/hooks/gallery'
import { useVideoHighlights } from '@/lib/api/hooks/video'
import { parseHtmlString, stripHtml } from '@/utils/helpers/html'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { getVideoPreviewImage } from '@afsa/shared/utils'

interface MultimediaSectionProps {
  className?: string;
  data: {
    [key: string]: WebPageSection
  }
}

const MultimediaSection = ({ className, data }: MultimediaSectionProps) => {
  const [activeTab, setActiveTab] = useState<'photos' | 'videos'>('photos')
  const { t } = useLanguage()
  const navigate = useNavigate()

  // Fetch photos/galleries - show highlights
  const { data: galleryHighlightsData } = useGalleryHighlights()

  // Fetch videos - show highlights
  const { data: videosData, isLoading: isVideosLoading } = useVideoHighlights()

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${day}/${month}/${year}`
  }


  // Default thumbnail for videos
  // const defaultVideoThumbnail = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png"

        const getPreviewImage = (previewUrl: string | undefined | null) => {
          if (previewUrl) {
              const image = getVideoPreviewImage(previewUrl)
              return image ? (
                  <img
                      src={image}
                      alt="Video Preview"
                      className="aspect-video  w-full object-cover"
                  
                  />
              ) : <FileVideo2 className="w-24 h-24 text-gray-300" />
           }
              return <FileVideo2 className="w-24 h-24 text-gray-300" />
  
          }

  // Transform videos data
  const videos = useMemo(() => {
    const videoList = (videosData?.data || [])
      .filter((video) => video.datePublished !== null)
    return videoList.map((video: { videoUploadId: string; title: string, description?: string, embeddedLink?: string, thumbnailUrl?: string | null }) => ({
      id: video.videoUploadId,
      title: video.title,
      description: video.description ? stripHtml(video.description) : '',
      // Use custom thumbnailUrl if available, otherwise use default placeholder
      // thumbnail: video.thumbnailUrl || defaultVideoThumbnail,
          thumbnail: getPreviewImage(video.embeddedLink || video.thumbnailUrl),
      duration: "0:00", // Duration not available in API
      category: "Multimedia",
      videoId: video.videoUploadId,
      embeddedLink: video.embeddedLink
    }))
  }, [videosData])

  // Default image for photos
  const defaultPhotoImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755763790/3b1a7b9af8bcf76fa2fe6ef9ea8328e56b2b5107_qetxu2.jpg"

  // Transform photos data
  const photos = useMemo(() => {
    // Gallery highlights return a flat array in data
    const photoList = galleryHighlightsData?.data || []

    return photoList.map((gallery: PhotoGallery, index: number) => ({
      id: gallery.galleryId || gallery.photoGalleryId || `gallery-${index}`,
      image: gallery.photoUrl || gallery.photoDetails?.[0]?.photoUrl || defaultPhotoImage,
      title: gallery.title,
      date: formatDate(gallery.datePublished || gallery.createdAt),
      folderId: gallery.folderId,
      folderName: gallery.folderName
    }))
  }, [galleryHighlightsData])

  const handlePhotoClick = (photo: { id: string; folderId?: string; folderName?: string }) => {
    // Navigate to photo gallery, optionally with folder filter
    if (photo.folderId && photo.folderName) {
      // Navigate to photo gallery with folder category
      navigate(`/media/photo-gallery/${encodeURIComponent(photo.folderName)}`)
    } else {
      navigate('/media/photo-gallery')
    }
  }

  const handleViewAllClick = () => {
    navigate('/media/photo-gallery')
  }

  const handlePlayVideo = (_videoTitle: string, _videoId?: string, embeddedLink?: string) => {
    if (embeddedLink) {
      // Open video in new tab or implement modal player
      window.open(embeddedLink, '_blank', 'noopener,noreferrer')
    } else {
      // Fallback: could navigate to video detail page if it exists
    }
  }

  // Image error handler
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, defaultImage: string) => {
    if (e.currentTarget.src !== defaultImage) {
      e.currentTarget.src = defaultImage
    }
  }

  return (
    <section className={`bg-white ${className}`} aria-labelledby="multimedia-title">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className=" mb-5">

          {/* Mobile Tabs */}
          <div className='pb-8 md:hidden flex justify-between'>
            <div>
              <div className="flex items-center">
                <button
                  onClick={() => setActiveTab('photos')}
                  className={`py-2 text-sm font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'photos'
                    ? 'text-gray-100 border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('photos')}
                </button>
                <button
                  onClick={() => setActiveTab('videos')}
                  className={`px-4 py-2 text-sm font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'videos'
                    ? 'text-black border-wine-100 '
                    : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                    }`}
                >
                  {t('videos')}
                </button>
              </div>
            </div>
            <div className='md:hidden'>
              <ViewAllButton onClick={handleViewAllClick} />
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <h2
              id="multimedia-title"
              className="text-3xl font-pt-serif text-gray-900"
            >
              {/* {t('multimedia')} */}
              {activeTab === 'photos' ? (data?.HOME_MULTIMEDIA_PHOTO?.title ?? t('multimedia')) : (data?.HOME_MULTIMEDIA_VIDEO?.title ?? t('multimedia'))}
            </h2>

            {/* Desktop Tab Buttons */}
            <div className="hidden sm:flex items-center">
              <button
                onClick={() => setActiveTab('photos')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'photos'
                  ? 'text-black border-wine-100 '
                  : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('photos')}
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-4 py-2 text-base font-medium font-satoshi border-b-2 transition-all duration-300 ease-in-out ${activeTab === 'videos'
                  ? 'text-black border-wine-100 '
                  : 'text-gray-600 border-transparent hover:border-gray-300 hover:text-gray-900'
                  }`}
              >
                {t('videos')}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between ">
            <p className=" font-satoshi max-w-2xl lg:w-[387px]">
              {/* {activeTab === 'photos' 
                ? 'Explore photos and highlights that capture AfSA\'s journey and impact across Africa\'s space landscape'
                : 'Explore videos and highlights that capture AfSA\'s journey and impact across Africa\'s space landscape'
              } */}
              <span dangerouslySetInnerHTML={parseHtmlString(activeTab === 'photos' ? (data?.HOME_MULTIMEDIA_PHOTO?.description ?? 'Explore photos and highlights that capture AfSA\'s journey and impact across Africa\'s space landscape') : (data?.HOME_MULTIMEDIA_VIDEO?.description ?? 'Explore videos and highlights that capture AfSA\'s journey and impact across Africa\'s space landscape'))} />
            </p>
            <div className='hidden md:block'>
              <ViewAllButton onClick={handleViewAllClick} />
            </div>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="block lg:hidden">
          {activeTab === 'videos' ? (
            <div>
              {/* Featured Video */}
              <div className="mb-6">
                {videos.length > 0 ? (
                  <div className="bg-white overflow-hidden">
                    <div className="relative">
                      {/* <img
                        src={videos[0].thumbnail}
                        alt={videos[0].title}
                        className="w-full h-48 object-cover"
                        onError={(e) => handleImageError(e, defaultVideoThumbnail)}
                      /> */}
                      {videos[0].thumbnail}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <button
                          onClick={() => handlePlayVideo(videos[0].title, videos[0].videoId, videos[0].embeddedLink)}
                          className="w-8 h-8 lg:w-10 lg:h-10 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                          aria-label={`Play video: ${videos[0].title}`}
                        >
                          <Play className="h-4 w-4 text-white fill-current" />
                        </button>
                      </div>

                      {/* Title Overlay */}
                      {/* <div className="absolute top-4 left-4 right-4">
                        <div className="p-3">
                          <p className="text-xs font-medium font-satoshi text-white bg-black/50 rounded px-2 py-1">
                            {videos[0].title}
                          </p>
                        </div>
                      </div> */}
                    </div>

                    {/* Video Description */}
                    <div className="py-4">
                      <h3 className="text-lg font-medium font-pt-serif text-gray-900 mb-2">
                        {videos[0].title}
                      </h3>
                      <p className="text-sm text-gray-600 font-satoshi leading-relaxed line-clamp-4">
                        {/* <span dangerouslySetInnerHTML={parseHtmlString(videos[0].description)} /> */}
                        {videos[0].description}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No videos available
                  </div>
                )}
              </div>

              {/* Horizontal Scrolling Videos */}
              {videos.length > 1 && (
                <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
                  {videos.slice(1).map((video) => (
                    <div key={video.id} className="flex-none w-72 snap-start">
                      <div className="bg-white overflow-hidden">
                        <div className="relative">
                          {video.thumbnail}
                          {/* <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-full h-40 object-cover"
                            onError={(e) => handleImageError(e, defaultVideoThumbnail)}
                          /> */}

                          {/* Play Button Overlay */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <button
                              onClick={() => handlePlayVideo(video.title, video.videoId, video.embeddedLink)}
                              className="w-12 h-12 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                              aria-label={`Play video: ${video.title}`}
                            >
                              <Play className="h-6 w-6 text-white fill-current ml-0.5" />
                            </button>
                          </div>

                          {/* Title Overlay */}
                          <div className="absolute top-2 left-2 right-2">
                            <div className="p-2">
                              <p className="text-xs font-medium font-satoshi text-white bg-black/50 rounded px-2 py-1">
                                {video.title}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Video Title */}
                        <div className="py-3">
                          <h4 className="text-base font-medium font-pt-serif text-gray-900 line-clamp-2">
                            {video.title}
                          </h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Mobile Photos Layout */
            <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
              {photos.length > 0 ? photos.map((photo: { id: string; title: string; image: string; date: string }) => (
                <div
                  key={photo.id}
                  className="flex-none w-64 snap-start cursor-pointer"
                  onClick={() => handlePhotoClick(photo)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePhotoClick(photo)
                    }
                  }}
                  aria-label={`View gallery: ${photo.title}`}
                >
                  <div className="bg-white overflow-hidden">
                    <img
                      src={photo.image}
                      alt={photo.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => handleImageError(e, defaultPhotoImage)}
                    />
                    <div className="py-3">
                      <p className="text-xs text-gray-500 font-satoshi mb-1">
                        {photo.date}
                      </p>
                      <p className="text-sm font-medium font-pt-serif text-gray-900">
                        {photo.title}
                      </p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500 text-sm w-full">
                  No photos available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop Content */}
        <div className="hidden lg:block">
          {activeTab === 'videos' ? (
            isVideosLoading ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    <LoadingSpinner message='Loading Videos' className="mx-auto" />
                  </div>
                ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
              {/* First Column - Large Video */}
              <div className=" ">
                  <div className="bg-white  overflow-hidden">
                    <div className="relative">
                      {videos[0].thumbnail}
                      {/* <img
                        src={videos[0].thumbnail}
                        alt={videos[0].title}
                        className="w-full lg:h-[300px] object-cover"
                        onError={(e) => handleImageError(e, defaultVideoThumbnail)}
                      /> */}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <button
                          onClick={() => handlePlayVideo(videos[0].title, videos[0].videoId, videos[0].embeddedLink)}
                          className="w-8 h-8 lg:w-10 lg:h-10 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                          aria-label={`Play video: ${videos[0].title}`}
                        >
                          <Play className="h-4 w-4 text-white fill-current" />
                        </button>
                      </div>

                      {/* Title Overlay */}
                      {/* <div className="absolute top-4 left-4 right-4">
                        <div className=" p-3">
                          <p className="text-xs font-medium font-satoshi text-gray-900">
                            {videos[0].title}
                          </p>
                        </div>
                      </div> */}
                    </div>

                    {/* Video Description */}
                    <div className="py-4 ">
                      <h3 className="text-lg font-medium font-pt-serif text-gray-900 mb-2">
                        {videos[0].title}
                      </h3>
                      <p className="text-sm text-gray-600 font-satoshi leading-relaxed line-clamp-4">
                        {/* <span dangerouslySetInnerHTML={parseHtmlString(videos[0].description)} /> */}
                        {videos[0].description}
                      </p>
                    </div>
                  </div>
              </div>

              {/* Second Column - Video with Description */}
              <div className="  ">
                {videos.length > 1 ? (
                  <div className="bg-white overflow-hidden">
                    <div className="relative">
                      {videos[1].thumbnail}
                      {/* <img
                        src={videos[1].thumbnail}
                        alt={videos[1].title}
                        className="w-full lg:h-[300px] object-cover"
                        onError={(e) => handleImageError(e, defaultVideoThumbnail)}
                      /> */}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <button
                          onClick={() => handlePlayVideo(videos[1].title, videos[1].videoId, videos[1].embeddedLink)}
                          className="w-8 h-8 lg:w-10 lg:h-10 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                          aria-label={`Play video: ${videos[1].title}`}
                        >
                          <Play className="h-4 w-4 text-white fill-current" />
                        </button>
                      </div>

                      {/* Title Overlay */}
                      {/* <div className="absolute top-4 left-4 right-4">
                        <div className=" p-3">
                          <p className="text-xs font-medium font-satoshi text-gray-900">
                            {videos[1].title}
                          </p>
                        </div>
                      </div> */}
                    </div>

                    {/* Video Description */}
                    <div className="py-4">
                      <h3 className="text-lg font-medium font-pt-serif text-gray-900 mb-2">
                        {videos[1].title}
                      </h3>
                      <p className="text-sm text-gray-600 font-satoshi leading-relaxed line-clamp-4">
                        {/* <span dangerouslySetInnerHTML={parseHtmlString(videos[1].description)} /> */}
                        {videos[1].description}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Third Column - Two Videos without Description */}
              <div className=" space-y-4 ">
                {videos.length > 2 ? videos.slice(2, 4).map((video) => (
                  <div key={video.id} className="bg-white  overflow-hidden">
                    <div className="relative">
                      {video.thumbnail}
                      {/* <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-32 lg:h-[194px] object-cover"
                        onError={(e) => handleImageError(e, defaultVideoThumbnail)}
                      /> */}

                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <button
                          onClick={() => handlePlayVideo(video.title, video.videoId, video.embeddedLink)}
                          className="w-12 h-12 bg-wine-100 hover:bg-wine-200 rounded-lg flex items-center justify-center transition-colors duration-300"
                          aria-label={`Play video: ${video.title}`}
                        >
                          <Play className="h-6 w-6 text-white fill-current ml-0.5" />
                        </button>
                      </div>

                      {/* Title Overlay */}
                      <div className="absolute top-2 left-2 right-2">
                        <div className="  p-2">
                          <p className="text-sm font-medium font-satoshi text-gray-900">
                            {video.title}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Only Title - No Description */}
                    <div className="py-1">
                      <h4 className="text-lg font-medium font-pt-serif text-gray-900">
                        {video.title}
                      </h4>
                    </div>
                  </div>
                )) : null}
              </div>
            </div> ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No video available
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {photos.length > 0 ? photos.map((photo: { id: string; title: string; image: string; date: string }) => (
                <div
                  key={photo.id}
                  className="bg-white overflow-hidden cursor-pointer"
                  onClick={() => handlePhotoClick(photo)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePhotoClick(photo)
                    }
                  }}
                  aria-label={`View gallery: ${photo.title}`}
                >
                  <img
                    src={photo.image}
                    alt={photo.title}
                    className="w-full h-[180px] object-cover"
                    onError={(e) => handleImageError(e, defaultPhotoImage)}
                  />
                  <div className="py-3">
                    <p className="text-xs text-gray-500 font-satoshi mb-1">
                      {photo.date}
                    </p>
                    <p className="text-sm font-medium font-pt-serif text-gray-900">
                      {photo.title}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-8 text-gray-500 text-sm">
                  No photos available
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default MultimediaSection